// Neuigkeiten: der Dialog nach einem Release und die Seite mit allen bisherigen Releases.
// Siehe docs/release-notes.md.
//
// Unter `releases` steht je Release-ID aus dem Backend (ReleaseNoteCatalog.RELEASES) ein Titel und
// je Highlight-ID ein Titel und ein Text. Der Text ist Produktkommunikation, kein Handbuch: was der
// Leser jetzt kann, in zwei Sätzen, aus seiner Sicht. Wohin der Knopf führt, steht in
// services/releaseNotes.js.
//
// Darunter darf ein Highlight `details` haben, sparsam: nur die wenigen Punkte, die jemand wissen muss
// und die nicht schon im Text stehen, je ein Begriff (`term`, so wie er auf dem Bildschirm steht) und
// ein Satz (`text`). Meist ohne Abschnittstitel; einer (`title`) nur, wo die Punkte etwas gemeinsam
// haben, das sie erst verständlich macht. Eine einfache Neuerung braucht keine Details. Keine
// geschweiften Klammern, kein senkrechter Strich und kein @: vue-i18n liest sie als Platzhalter, Plural
// und Verweis.
export default {
  title: "Neuigkeiten",
  subtitle: "Was sich in Lewara getan hat: neue Funktionen und Verbesserungen",
  empty: "Noch keine Neuigkeiten",
  unseen: "Neu",
  contents: "Inhalt",
  count: "{n} Neuerung | {n} Neuerungen",
  // Ob die Funktion im Tarif des Arbeitsbereichs steckt. Steht an jeder Karte, auch bei Mitarbeitern,
  // die nichts buchen können: sie sollen wissen, ob sie es schon haben oder erst jemand buchen muss.
  plan: {
    included: "In Ihrem Tarif enthalten",
    not_included: "Nicht in Ihrem Tarif enthalten",
  },
  open: "Ansehen",
  // Das Standbild im Changelog und im Dialog ist klein; ein Klick zeigt es in voller Größe.
  enlarge: "Bild vergrößern",
  shrink: "Bild schließen",
  dialog: {
    eyebrow: "Neu in Lewara",
    title: "Das ist neu, {name}",
    title_anonymous: "Das ist neu",
    text: "Seit Ihrem letzten Besuch ist Lewara um einiges gewachsen. Die Highlights im Überblick:",
    contents: "Inhalt",
    all: "Alle Neuigkeiten",
    confirm: "Verstanden",
    close: "Neuigkeiten schließen",
  },
  releases: {
    RELEASE_2026_09_28: {
      title: "Zeiterfassung, Leistungskatalog und vieles mehr",
      highlights: {
        TIME_TRACKING: {
          title: "Zeiterfassung",
          text:
            "Stempeln Sie mit einem Klick ein und aus und buchen Sie die Zeit direkt auf das Projekt. " +
            "Im Büro prüfen und geben Sie die Zeiten aller Mitarbeiter auf einer Seite frei, fertig für " +
            "die Lohnabrechnung.",
          details: {
            links: {
              title: "Auch hier zu finden",
              items: {
                settings: {
                  term: "Einstellungen",
                  text: "Arbeitszeitmodelle mit Wochenstunden, Pausenregeln und Schichten.",
                },
                employee: {
                  term: "Mitarbeiter",
                  text: "Jeder Mitarbeiter bekommt sein Arbeitszeitmodell und einen Anfangssaldo.",
                },
                project: {
                  term: "Projekt",
                  text: "Der Tab Zeiten zeigt die gestempelten Stunden und gleicht sie mit den Berichten ab.",
                },
              },
            },
          },
        },
        SERVICE_CATALOG: {
          title: "Leistungskatalog statt Montage",
          text:
            "Das Leistungsverzeichnis Ihres Auftrags steht jetzt im Projekt, mit dem Fortschritt jeder " +
            "Position. [[cr:Im/In der/Im]] [[cr]] buchen Sie die erbrachte Menge direkt auf die Position.",
          details: {
            main: {
              items: {
                gaeb: {
                  term: "GAEB importieren",
                  text: "Leistungsverzeichnisse als X83, X84 oder X86 übernehmen.",
                },
                change_orders: {
                  term: "Nachträge",
                  text: "Nur beauftragte Nachträge zählen zum Auftragswert.",
                },
                migrated: {
                  term: "Bestehende Stücklisten",
                  text: "Sind als eigener Titel in den Leistungskatalog übernommen.",
                },
              },
            },
          },
        },
        PURCHASE_PRICES: {
          title: "Mehrere Einkaufspreise",
          text:
            "Hinterlegen Sie für Maschinen und Material mehrere Einkaufspreise, etwa je Lieferant, und " +
            "wählen Sie beim Buchen den passenden. Der Preis wird mit dem Bericht festgeschrieben, spätere " +
            "Änderungen verschieben die Nachkalkulation nicht.",
          details: {
            main: {
              items: {
                project: {
                  term: "Im Projekt",
                  text: "Jedes Projekt hat eine eigene Preisliste und zeigt unter jedem Preis, wo die Stammdaten abweichen.",
                },
                migrated: {
                  term: "Nach dem Update",
                  text: "Ihr bisheriger Preis ist jetzt der Standardpreis, an Ihren Zahlen ändert sich nichts.",
                },
              },
            },
          },
        },
        MACHINE_UNITS: {
          title: "Maschinen nach Stunde oder Tag",
          text:
            "Jede Maschine rechnet jetzt pro Stunde oder pro Tag ab, und ihr Einsatz wird in Stunden " +
            "erfasst. So stimmen die Kosten auch, wenn eine Maschine nur einen Teil der Schicht läuft.",
          details: {
            main: {
              items: {
                unit: {
                  term: "Abrechnungseinheit",
                  text: "An der Maschine eine Auswahl aus Stunde oder Tag statt Freitext.",
                },
                figures: {
                  term: "Gut zu wissen",
                  text: "Maschinen nach Stunde rechnen [[cr:im/in der/im]] [[cr]] jetzt Schichtstunden mal Preis, auch in älteren Berichten.",
                },
              },
            },
          },
        },
        REPORTS: {
          title: "[[cr.pl]] und [[hwr.pl]]",
          text:
            "[[hwr.pl]] hängen jetzt [[cr:am/an der/am]] [[cr]], damit die Nachkalkulation dieselbe Arbeit " +
            "nicht doppelt zählt. Dazu zeigen beide Tabs einen Kalender mit dem Druckstatus Ihrer Berichte.",
          details: {
            main: {
              items: {
                categories: {
                  term: "Kategorien",
                  text: "Wählen oder legen Sie direkt [[hwr:im/in der/im]] [[hwr]] an.",
                },
                default_on: {
                  term: "Gut zu wissen",
                  text: "Anhängen ist bei allen Projekten eingeschaltet. Zählt die Arbeit aus [[hwr.datpl]] bei Ihnen zusätzlich, schalten Sie es im Projekt unter Konfiguration aus.",
                },
              },
            },
          },
        },
        BUDGET_HEALTH: {
          title: "Budget-Ampel nach Fertigstellung",
          text:
            "Die Ampel vergleicht jetzt das verbrauchte Budget mit dem Fertigstellungsgrad des Projekts. " +
            "So warnt Lewara, sobald die Kosten der Leistung davonlaufen, und nicht erst, wenn das Budget " +
            "aufgebraucht ist.",
          details: {
            main: {
              items: {
                completion: {
                  term: "Fertigstellungsgrad",
                  text: "Kommt aus dem Leistungskatalog oder wird im Budget-Dialog von Hand eingetragen.",
                },
              },
            },
          },
        },
        PROJECT_LIST: {
          title: "Neue Projektliste",
          text:
            "Die Projektliste hat ein neues Aussehen und zeigt Kunde, Ort, Zeitraum, Leitung und Budget auf " +
            "einen Blick. Ein Rechtsklick klappt eine Zeile mit ihren Zahlen auf.",
        },
        PACKETS: {
          title: "Pakete im Katalog",
          text:
            "Pakete stehen jetzt direkt in den Listen von Materialien, Maschinen und Zusatzleistungen. Aus " +
            "jedem Eintrag wird mit „Paket daraus erstellen“ ein neues Paket.",
        },
        FILE_BROWSER: {
          title: "Alle Dateien an einer Stelle",
          text:
            "Jede abgelegte Datei, ob am Projekt, beim Kunden oder an der Maschine, finden Sie jetzt unter " +
            "Dateien, mit einer Suche über alle Ordner. Nachweise bekommen ein Ablaufdatum, und Lewara " +
            "erinnert rechtzeitig.",
        },
        ACTIVITY_LOG: {
          title: "Protokoll an jedem Datensatz",
          text:
            "Projekte, Kunden, Mitarbeiter, Stammdaten und Berichte haben einen Tab Protokoll. Dort sehen " +
            "Sie, wer was wann geändert hat und wo der Datensatz noch verwendet wird.",
        },
        PERSONNEL_PLANNING: {
          title: "Personalplanung",
          text:
            "Die Gesamtplanung zeigt alle Projekte in einem Kalender, der neue Projekte von selbst aufnimmt. " +
            "Die Kapazität sehen Sie jetzt je Phase, und Zoom und Vollbild machen große Planungen übersichtlich.",
        },
      },
    },
    RELEASE_2026_07_23: {
      title: "Mitlaufende Nachkalkulation",
      highlights: {
        PROJECT_COSTS: {
          title: "Mitlaufende Nachkalkulation",
          text:
            "Verfolgen Sie Auftragsvolumen, gebuchte Kosten und die erwartete Marge Ihrer Projekte " +
            "laufend und lassen Sie sich warnen, bevor ein Projekt ins Minus läuft.",
        },
      },
    },
  },
}
